import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Shield,
  Users,
  Check,
  X,
  Plus,
  Trash2,
  AlertCircle,
} from 'lucide-react';
import { adminApi, ALL_ROLES, UserRole, UserRoleRecord, Permission } from '../api/client';
import { useRole } from '../contexts/RoleContext';
import { Button } from '../components/common/Button';

const roleLabels: Record<UserRole, string> = {
  superadmin: 'Super Admin',
  sales_manager: 'Sales Manager',
  exec: 'Executive',
  sales: 'Sales',
  asst: 'Assistant',
  viewer: 'Viewer',
  unauthorized: 'Unauthorized',
};

const roleColors: Record<UserRole, string> = {
  superadmin: 'bg-purple-100 text-purple-800',
  sales_manager: 'bg-blue-100 text-blue-800',
  exec: 'bg-green-100 text-green-800',
  sales: 'bg-yellow-100 text-yellow-800',
  asst: 'bg-gray-100 text-gray-800',
  viewer: 'bg-gray-100 text-gray-600',
  unauthorized: 'bg-red-100 text-red-800',
};

function PermissionsMatrix() {
  const queryClient = useQueryClient();
  const [updating, setUpdating] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'permissions'],
    queryFn: () => adminApi.getPermissions(),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      role,
      permission,
      granted,
    }: {
      role: UserRole;
      permission: Permission;
      granted: boolean;
    }) => adminApi.updatePermission(role, permission, granted),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'permissions'] });
      setUpdating(null);
    },
    onError: () => {
      setUpdating(null);
    },
  });

  const handleToggle = (role: UserRole, permission: Permission, currentValue: boolean) => {
    if (role === 'superadmin') return; // Can't modify superadmin
    const key = `${role}-${permission}`;
    setUpdating(key);
    updateMutation.mutate({ role, permission, granted: !currentValue });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-primary border-r-transparent" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-red-600 py-4">Failed to load permissions matrix</div>
    );
  }

  const { matrix, permissions, roles } = data;
  const displayRoles = roles.filter(r => r !== 'unauthorized');

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Click checkboxes to toggle permissions</p>
          <p className="mt-1">
            Changes take effect immediately. Super Admin permissions cannot be modified.
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50">
                Permission
              </th>
              {displayRoles.map((role) => (
                <th
                  key={role}
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {roleLabels[role]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {permissions.map((permission) => (
              <tr key={permission} className="hover:bg-gray-50">
                <td className="px-4 py-2 whitespace-nowrap text-sm font-mono text-gray-900 sticky left-0 bg-white">
                  {permission}
                </td>
                {displayRoles.map((role) => {
                  const isGranted = matrix[role]?.[permission] ?? false;
                  const key = `${role}-${permission}`;
                  const isUpdating = updating === key;
                  const isSuperadmin = role === 'superadmin';

                  return (
                    <td key={role} className="px-4 py-2 text-center">
                      {isUpdating ? (
                        <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-brand-primary border-r-transparent" />
                      ) : (
                        <button
                          onClick={() => handleToggle(role, permission as Permission, isGranted)}
                          disabled={isSuperadmin || updateMutation.isPending}
                          className={`p-1 rounded transition-colors ${
                            isSuperadmin
                              ? 'cursor-not-allowed opacity-50'
                              : 'hover:bg-gray-100 cursor-pointer'
                          }`}
                          title={
                            isSuperadmin
                              ? 'Super Admin has all permissions'
                              : `Click to ${isGranted ? 'revoke' : 'grant'}`
                          }
                        >
                          {isGranted ? (
                            <Check className="w-5 h-5 text-green-600 inline-block" />
                          ) : (
                            <X className="w-5 h-5 text-gray-300 inline-block" />
                          )}
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {updateMutation.isError && (
        <div className="text-red-600 text-sm mt-2">
          Failed to update permission. Please try again.
        </div>
      )}
    </div>
  );
}

function UserManagement() {
  const queryClient = useQueryClient();
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<UserRole>('viewer');
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => adminApi.getUsers(),
  });

  const addMutation = useMutation({
    mutationFn: ({ email, role }: { email: string; role: UserRole }) =>
      adminApi.setUserRole(email, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setNewEmail('');
      setNewRole('viewer');
      setShowAddForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (email: string) => adminApi.deleteUserRole(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setDeleteConfirm(null);
    },
  });

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (newEmail && newRole) {
      addMutation.mutate({ email: newEmail.toLowerCase().trim(), role: newRole });
    }
  };

  const handleUpdateRole = (email: string, role: UserRole) => {
    addMutation.mutate({ email, role });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-primary border-r-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 py-4">Failed to load users</div>
    );
  }

  const users = data?.users || [];

  return (
    <div className="space-y-4">
      {/* Add User Form */}
      {showAddForm ? (
        <form onSubmit={handleAdd} className="bg-gray-50 rounded-lg p-4 space-y-4">
          <h4 className="font-medium text-font-primary">Add/Update User Role</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="user@wayfong.com"
                className="input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as UserRole)}
                className="input"
              >
                {ALL_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {roleLabels[role]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <Button type="submit" isLoading={addMutation.isPending}>
                Save
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
          {addMutation.isError && (
            <p className="text-red-600 text-sm">
              Failed to save user role. Please try again.
            </p>
          )}
        </form>
      ) : (
        <Button onClick={() => setShowAddForm(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      )}

      {/* Users Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Email
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Role
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Added By
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Updated
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user: UserRoleRecord) => (
              <tr key={user.email} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {user.email}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <select
                    value={user.role}
                    onChange={(e) => handleUpdateRole(user.email, e.target.value as UserRole)}
                    className={`text-sm rounded-full px-3 py-1 font-medium ${roleColors[user.role]}`}
                  >
                    {ALL_ROLES.map((role) => (
                      <option key={role} value={role}>
                        {roleLabels[role]}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {user.created_by || 'system'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {new Date(user.updated_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  {deleteConfirm === user.email ? (
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-sm text-red-600">Delete?</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteMutation.mutate(user.email)}
                        isLoading={deleteMutation.isPending}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        Yes
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteConfirm(null)}
                      >
                        No
                      </Button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(user.email)}
                      className="text-gray-400 hover:text-red-600 p-1"
                      title="Remove user"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  No users configured. Add users to pre-authorize them.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3">
        <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-yellow-800">
          <p className="font-medium">Note about default access:</p>
          <p className="mt-1">
            Users with @wayfong.com email addresses who are not listed here will
            automatically get <strong>Viewer</strong> role (read-only access).
            Add users here to give them elevated permissions.
          </p>
        </div>
      </div>
    </div>
  );
}

export function Admin() {
  const { actualRole } = useRole();
  const [activeTab, setActiveTab] = useState<'users' | 'permissions'>('users');

  // Only superadmin can access this page (use actualRole to ignore impersonation)
  if (actualRole !== 'superadmin') {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-font-primary mb-2">
            Access Denied
          </h1>
          <p className="text-font-secondary">
            Only superadmins can access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-serif font-semibold text-font-primary flex items-center gap-2">
          <Shield className="w-6 h-6" />
          Admin
        </h1>
        <p className="text-font-secondary mt-1">
          Manage user roles and view permissions
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'users'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="w-4 h-4 inline-block mr-2" />
            User Management
          </button>
          <button
            onClick={() => setActiveTab('permissions')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'permissions'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Shield className="w-4 h-4 inline-block mr-2" />
            Permissions Matrix
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="card p-6">
        {activeTab === 'users' && <UserManagement />}
        {activeTab === 'permissions' && <PermissionsMatrix />}
      </div>
    </div>
  );
}
